import React, {FC} from 'react'
import {flowMax, addDisplayName, addProps, addWrapper} from 'ad-hok'
import parseISO from 'date-fns/parseISO'

import {addClasses} from 'theme'
import Form from 'components/Form'
import Grid from 'components/Grid'
import {editCharityCareApplicationFormSchema} from 'components/EditCharityCareApplicationForm/schema'
import FormSection from 'components/FormSection'
import ReadOnlyTextField from 'components/ReadOnlyTextField'
import ApplicationStatusUpdateSelect from 'components/ApplicationStatusUpdateSelect'
import MultilineTextField from 'components/MultilineTextField'
import ApplicationFormLeftColumn from 'components/EditApplicationForm/ApplicationFormLeftColumn'
import DateField from 'components/DateField'
import SelectField from 'components/SelectField'
import OutcomesSection from 'components/EditApplicationForm/OutcomesSection'
import {addApplicationFormContext} from 'components/EditApplicationForm/applicationFormContext'
import HouseholdSection from 'components/EditApplicationForm/HouseholdSection'
import DocumentRequestSection from 'components/EditApplicationForm/DocumentRequestSection'
import CommunicationHistorySection from 'components/EditApplicationForm/CommunicationHistorySection'
import {addFormikTyped} from 'utils/form/formik'
import {addApplicationBenefitOutcomesContextProvider} from 'components/EditApplicationForm/applicationBenefitOutcomesContext'
import {applicationFormClasses} from 'components/EditMedicaidApplicationForm/index'
import DataAndDocumentsSection from 'components/EditApplicationForm/DataAndDocumentsSection'
import TextField from 'components/TextField'
import {Application_application_CharityCareApplication} from 'graphql/deserializedTypes/Application'
import {Assert, SchemaDoesntHaveExtraFields} from 'utils/form/typeHelpers'
import {addUpdateApplicationMutation} from 'graphql/generated'
import AccountOnDateOfServiceDetails from 'components/AccountOnDateOfServiceDetails'
import AssignedToField from 'components/AssignedToField'
import FileTemplateSection from 'components/FileTemplateSection'
import EditableFilesSection from 'components/EditableFilesSection'
import EventLogSection from 'components/EditApplicationForm/EventLogSection'

type Check = Assert<
  SchemaDoesntHaveExtraFields<
    typeof editCharityCareApplicationFormSchema,
    typeof addUpdateApplicationMutation
  >
>

const EditCharityCareApplicationForm: FC = flowMax(
  addDisplayName('EditCharityCareApplicationForm'),
  addApplicationFormContext,
  addProps(({application}) => ({
    application: application as Application_application_CharityCareApplication,
  })),
  addProps(
    ({application}) => ({
      initialValues: {
        application: {
          ...application,
          assignedToId: application.assignedTo?.id ?? null,
        },
      },
    }),
    ['application']
  ),
  addClasses(applicationFormClasses),
  addWrapper(
    (
      render,
      {classes, onUpdateSuccess, mutateUpdateApplication, initialValues}
    ) => (
      <Form
        name="applicationForm"
        className={classes.form}
        schema={editCharityCareApplicationFormSchema}
        initialValues={initialValues}
        mutate={mutateUpdateApplication}
        onSubmitSuccess={onUpdateSuccess}
        shouldResetFormOnInitialValuesChange
        resetOnSaveSuccess
      >
        {render()}
      </Form>
    )
  ),
  addFormikTyped(editCharityCareApplicationFormSchema),
  addProps(
    ({
      application: {person},
      formik: {
        values: {
          application: {initialDateOfService},
        },
      },
    }) => ({
      initialDateOfService: initialDateOfService
        ? parseISO(initialDateOfService)
        : null,
      outcomesPeople: [person],
    })
  ),
  addApplicationBenefitOutcomesContextProvider,
  ({application, refetchQueriesOnStatusChange, classes}) => (
    <Grid container className={classes.container} direction="row">
      <ApplicationFormLeftColumn application={application} />
      <Grid item className={classes.formColumnContainer}>
        <FormSection
          labelTranslationKey="applicationProcessing"
          shouldMemoize={false}
        >
          <ReadOnlyTextField name="application.id" />
          <SelectField name="application.charityCareType" />
          <DateField name="application.initialDateOfService" />
          {application.accountOnDateOfService ? (
            <ReadOnlyTextField name="application.accountNumber" />
          ) : (
            <TextField name="application.accountNumber" />
          )}
          <AccountOnDateOfServiceDetails application={application} />
          <DateField name="application.dateOfApplication" />
          <DateField name="application.requestedDateOfApplication" />
          <TextField type="number" name="application.coveragePeriod" />
        </FormSection>
        <HouseholdSection />
        <FileTemplateSection />
        <EditableFilesSection />
        <OutcomesSection />
        <DataAndDocumentsSection />
      </Grid>
      <Grid item className={classes.rightColumnContainer}>
        <ApplicationStatusUpdateSelect
          className={classes.statusSelect}
          application={application}
          refetchQueries={refetchQueriesOnStatusChange}
        />
        <AssignedToField
          name="application.assignedToId"
          assignable={application}
        />
        <MultilineTextField rows={2} rowsMax={20} name="application.notes" />
        <EventLogSection />
        <DocumentRequestSection />
        <CommunicationHistorySection />
      </Grid>
    </Grid>
  )
)

export default EditCharityCareApplicationForm
